import React from "react";
import { ChartTypes, DateUnits, ISeries, ITrendDataLimit, TrendMarkerTypes } from '@iom/trend';
import { Trend } from "@iom/trend";
import _ from 'lodash';
import { MeasurementSetting } from "../../models/settings/MeasurementSettings";
import utility from "../shared/Utility";
import IThresholdSettings from "../../models/settings/ThresholdSettings";
import NoTrendToDisplay from "../shared/NoTrendToDisplay";

interface IReportProps {
    sampleData?: any;
    columns?: any[];
    data?: any[];
    rowData?: any;
    settings: MeasurementSetting;
    thresholdSettings: IThresholdSettings[];
}

interface IState {
    series: ISeries[],
    chartType: ChartTypes,
    dateUnit: DateUnits,
    dateUnitCount: number,
    start?: Date,
    end?: Date
}
export default class ReportTrendGraph extends React.Component<IReportProps, IState> {
    start = new Date(2019, 0, 1, 0, 0, 0, 0);
    end = new Date(2019, 0, 5, 0, 0, 0, 0);
    private _mount: boolean;
    private minXValue: number;
    private maxXValue: number;

    constructor(props: IReportProps) {
        super(props);
        console.log(this.props.rowData);
        let xValue: number[] = [this.props.rowData.alarmLow1, this.props.rowData.alarmLow2, this.props.rowData.alarmHigh1, this.props.rowData.alarmHigh2, this.props.rowData.rejectLow, this.props.rowData.rejectHigh]
        this.props.rowData?.readings?.sort(utility.sortAlphaNum)?.forEach((item: any) => {
            xValue.push(item.positionMean)
        })

        this.minXValue = _.min(xValue) - (_.min(xValue) / 100 * 5);
        this.maxXValue = _.max(xValue) + (_.max(xValue) / 100 * 5);

        this.state = {
            chartType: this.props.settings?.selectedGraphType.name,
            dateUnit: 'Day',
            dateUnitCount: 4,
            start: this.start,
            end: this.end,
            series: [
                {
                    name: 'Position',
                    color: '#5ABFAE',
                    id: '1',
                    markerStyle: {
                        type: this.state?.chartType ==="Bar" ? "None" : "Circle"
                    },
                    yAxis: {
                        min: this.minXValue,
                        max: this.maxXValue,
                        decimalPlaces: this.props.rowData?.decimals
                    },
                    dataLimits: [
                        {
                            name: 'Alarm Low Limit-1',
                            shownInChart: true,
                            value: this.props.rowData?.alarmLow1,
                            className: "custom-line-graph",
                            style: { stroke: this.props.thresholdSettings?.find(x => x.name === "Alarm Low 1" || x.name === "Alarm Limit1")?.colorCode }
                        },
                        {
                            name: 'Alarm High Limit-1',
                            shownInChart: true,
                            value: this.props.rowData?.alarmHigh1,
                            className: "custom-line-graph",
                            style: { stroke: this.props.thresholdSettings?.find(x => x.name === "Alarm High 1" || x.name === "Alarm Limit1")?.colorCode }
                        },
                        {
                            name: 'Alarm Low Limit-2',
                            shownInChart: true,
                            value: this.props.rowData?.alarmLow2,
                            className: "custom-line-graph",
                            style: { stroke: this.props.thresholdSettings?.find(x => x.name === "Alarm Low 2" || x.name === "Alarm Limit2")?.colorCode }
                        },
                        {
                            name: 'Alarm High Limit-2',
                            shownInChart: true,
                            value: this.props.rowData?.alarmHigh2,
                            className: "custom-line-graph",
                            style: { stroke: this.props.thresholdSettings?.find(x => x.name === "Alarm High 2" || x.name === "Alarm Limit2")?.colorCode }
                        },
                        {
                            name: 'Reject Low',
                            shownInChart: true,
                            value: this.props.rowData?.rejectLow,
                            className: "custom-line-graph",
                            style: { stroke: this.props.thresholdSettings?.find(x => x.name === "Reject Low" || x.name === "Reject Limit")?.colorCode }
                        },
                        {
                            name: 'Reject High',
                            shownInChart: true,
                            value: this.props.rowData?.rejectHigh,
                            className: "custom-line-graph",
                            style: { stroke: this.props.thresholdSettings?.find(x => x.name === "Reject High" || x.name === "Reject Limit")?.colorCode }
                        }
                    ],
                    isShownInChart: true,
                    points: this.generateOrdinalData(this.props.rowData) // String
                }
            ]
        }
    }


    onDateRangeChange = (start: string | number | Date, end: string | number | Date, dateUnit: DateUnits, dateUnitCount: number, isOnZoom: boolean) => {
        this.setState({
            dateUnit,
            dateUnitCount,
            start: start as Date,
            end: end as Date
        });
    }


    onDataLimitVisibilityChange = (series: ISeries, limit: ITrendDataLimit, isShown: boolean) => {
        let updatedValue = this.state.series.map((item) => {
            if (item.name === series.name) {
                let limits = item.dataLimits.map(((line: ITrendDataLimit) => {
                    if (line.name === limit.name) {
                        return { ...line, shownInChart: isShown };
                    } else {
                        return line;
                    }
                }))
                return { ...item, dataLimits: limits }
            } else {
                return item;
            }
        });

        this.setState({ series: updatedValue });
    }

    onChartTypeChange = (chartType: ChartTypes) => {
        this.setState({
            chartType: chartType
        });
        return chartType;
    }

    onSeriesChartTypeChange = (value: string, series: ISeries) => {
        let updatedValue: ISeries[] = this.state.series.map((item) => {
            if (item.name === series.name) {
                return { ...item, chartType: value } as ISeries;
            } else {
                return item;
            }
        });
        this.setState({ series: updatedValue });
    }

    onSeriesColorChange = (value: string, series: ISeries) => {
        let updatedValue: ISeries[] = this.state.series.map((item) => {
            if (item.name === series.name) {
                return { ...item, color: value } as ISeries;
            } else {
                return item;
            }
        });
        this.setState({ series: updatedValue });
    }

    getMarkerStyles = (value: string, item: ISeries) => {
        let marker = item.markerStyle ? { ...item.markerStyle } : {};
        if (value === "Circle") {
            marker.type = "Circle";
            marker.fillColor = "black";
        } else if (value === "Square") {
            marker.type = "Square";
            marker.fillColor = "white";
        } else if (value === "FillCircle") {
            marker.type = "Circle";
            marker.fillColor = "SeriesColor";
        } else if (value === "FillSquare") {
            marker.type = "Square";
            marker.fillColor = "SeriesColor";
        } else {
            marker.type = "None";
        }
        return marker;
    }

    onSeriesMarkerChange = (value: TrendMarkerTypes, series: ISeries) => {
        let updatedValue: ISeries[] = this.state.series.map((item) => {
            if (item.name === series.name) {
                return { ...item, markerStyle: { ...series.markerStyle, type: value } } as ISeries;
            } else {
                return item;
            }
        });
        this.setState({ series: updatedValue });
    }

    onSeriesFillTypeChange = (value: string, series: ISeries) => {
        let updatedValue: ISeries[] = this.state.series.map((item) => {
            if (item.name === series.name) {
                return { ...item, fillType: value } as ISeries;
            } else {
                return item;
            }
        });
        this.setState({ series: updatedValue });
    }

    onInvalidPointTypeChange = (value: string, series: ISeries) => {
        let updatedValue: ISeries[] = this.state.series.map((item) => {
            if (item.name === series.name) {
                return { ...item, invalidPointLineType: value } as ISeries;
            } else {
                return item;
            }
        });
        this.setState({ series: updatedValue });
    }


    generateOrdinalData(data: any) {
        let datas: any[] = []

        if (data.propertyType == "Calculated") {
            data.readings.forEach((item: any) => {
                if (item.countValues) {
                    item.countValues.forEach((value: any) => {
                        if (value.count == 0) {
                            datas.push({
                                xValue: "Calculated",
                                value: value.value
                            });
                        } else {
                            datas.push({
                                xValue: value.count?.toString(),
                                value: value.value != null ? value.value : 0
                            })
                        }
                    })
                }
            })
        } else {
            data?.readings?.forEach((item: any) => {
                datas.push({
                    xValue: item.positionName,
                    value: item.positionMean != null ? item.positionMean : 0
                })
            })
        }

        if (datas.length != 0) {
                datas.push({ xValue: "", value: null })
        }
        return datas;
    }

    render() {
        return <React.Fragment>
            <tr key={Math.random()}>
                <td colSpan={10}>
                    {this.state.series[0].points?.length > 0 ? <Trend
                        chartType={this.state.chartType}
                        invalidPointLineType='None'
                        invalidStatuses={['Invalid']}
                        dateUnit={this.state.dateUnit}
                        dateUnitCount={this.state.dateUnitCount}
                        gridLineColor='grey'
                        gridLineWidth={0.5}
                        showVerticalGridLines={false}
                        showHorizontalGridLines={true}
                        series={this.state.series}
                        xAxis={{ type: 'Ordinal', tickAngle: 45, height: 60 }}
                        onChartTypeChange={this.onChartTypeChange}
                        onDataLimitVisibilityChange={this.onDataLimitVisibilityChange}
                        onDateRangeChange={this.onDateRangeChange}
                        onInvalidPointTypeChange={this.onInvalidPointTypeChange}
                        onSeriesChartTypeChange={this.onSeriesChartTypeChange}
                        onSeriesColorChange={this.onSeriesColorChange}
                        onSeriesFillTypeChange={this.onSeriesFillTypeChange}
                        onSeriesMarkerChange={this.onSeriesMarkerChange}
                        trendChartHeight={200}
                        showChartType={false}
                        showDateTimePicker={false}
                        showOuterBorder={false}
                        showTableData={false}
                        yAxisTicksCount={2}
                        yAxisWidth={75}
                    />
                        : <div>
                            <NoTrendToDisplay></NoTrendToDisplay>
                        </div>}
                </td>
            </tr>
        </React.Fragment>
    }

}